import {
  fetchDashboardExperience,
  type DashboardExperienceResponse,
} from '@library/api-dashboard-experience'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import useSWR from 'swr'

import { ApiKeys } from '../configs'

type UseDashboardExperienceResult = {
  page_title: DashboardExperienceResponse['page_title']
  order_tabs: DashboardExperienceResponse['order_tabs']
  error: Error | undefined
  isLoading: boolean
}

const initialState: UseDashboardExperienceResult = {
  page_title: '',
  order_tabs: [],
  error: undefined,
  isLoading: true,
}

const getStatusCode = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status

    if (typeof status === 'number') {
      return status
    }
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message

    if (typeof message === 'string') {
      const statusMatch = message.match(/status=(\d+)/)

      if (statusMatch) {
        return Number(statusMatch[1])
      }
    }
  }

  return undefined
}

const useDashboardExperience = (): UseDashboardExperienceResult => {
  const { data, error } = useSWR(ApiKeys.DashboardExperience, fetchDashboardExperience)

  if (error) {
    const errorMessage = `Error fetching dashboard experience in myHome ${JSON.stringify(error)}`
    const statusCode = getStatusCode(error)

    datadogLogs.logger.error(errorMessage)
    datadogRum.addError(error, {
      page: 'MyHomePage',
      errorMessage: errorMessage,
    })
    if (statusCode && statusCode !== 200) {
      datadogRum.addAction('dashboard_experience_api_error', {
        page: 'MyHomePage',
        statusCode,
      })
    }

    return {
      ...initialState,
      error,
      isLoading: false,
    }
  }

  if (!data) {
    return initialState
  }

  return {
    page_title: data.page_title,
    order_tabs: data.order_tabs ?? initialState.order_tabs,
    error,
    isLoading: false,
  }
}

export { useDashboardExperience }
